// The Rival Ladder (对手阶梯) — the rules, with no DOM and no network attached
// (same split as game/revive.ts vs ui/revive.ts). TECH_SPEC §4bis.21.
//
// Two independent things live here:
//
//   1. `ladderEnabled` — the dark-rollout gate. The server hands the client a
//      percentage (`ladderPct`, default 0); a device is in when its hash bucket
//      falls below it. At 0 nobody is in, nothing is fetched, and the result
//      screen is exactly the shipped one.
//   2. `ladderRungs` — which of the three rungs a given result screen offers,
//      and what each of them is asking the player to make up.
//
// Both are pure functions so the parts that decide can be tested without a
// browser, a server or a run.

import { hashString, mix32 } from './rng';

/**
 * Rollout salt. Deliberately NOT `card-ab-v1` (src/experiment.ts): the same
 * device id under a different salt gives an *independent* assignment, so a
 * device being in the share-card `board` arm tells you nothing about whether it
 * sees the ladder. Two experiments, two uncorrelated partitions — the client
 * test pins that on a golden vector.
 */
export const LADDER_ID = 'rival-ladder-v1';

/** Stable bucket 0–99 for a device. `mix32(fnv1a("rival-ladder-v1:" + id)) % 100`. */
export function ladderBucket(deviceId: string): number {
  return mix32(hashString(`${LADDER_ID}:${deviceId}`)) % 100;
}

/**
 * Is this device in the ladder arm? `pct` is the server's `ladderPct`
 * (0 = dark, 100 = everyone). Anything unparseable is treated as 0: the feature
 * fails closed on both sides of the wire.
 */
export function ladderEnabled(deviceId: string, pct: number | undefined | null): boolean {
  if (typeof pct !== 'number' || !Number.isFinite(pct) || pct <= 0) return false;
  if (pct >= 100) return true;
  return ladderBucket(deviceId) < pct;
}

// ---------------------------------------------------------------------------
// Rungs
// ---------------------------------------------------------------------------

/** `opponent` = 刚才的对手, `self` = 本周的你, `daily` = 今日路人. */
export type RungId = 'opponent' | 'self' | 'daily';

/** One rival on the ladder, or (rung 2 only) the invitation to become one. */
export interface Rung {
  id: RungId;
  /** Their name, when the rung has one. `self` never does — it is you. */
  nickname: string | null;
  /** The score to beat; null only on the empty `self` rung. */
  scoreM: number | null;
  /**
   * Metres to make up, or null when there is nothing to make up — you are
   * already past them (or there is no target at all). Rung 1 turns that into
   * 「再赢一次」and every other rung into a plain score line.
   */
  gapM: number | null;
  /** False = shown, not tappable: the 「先留下一个成绩」 state. */
  clickable: boolean;
  /**
   * `self` only: this rung IS the run just finished (H02 B3). The row says so
   * («刚跑出的 {n} 米») rather than looking like an older record — not knowing
   * whether the number was fresh is the whole of the owner's complaint.
   */
  justSet?: boolean;
}

export interface RivalRef {
  nickname: string;
  scoreM: number;
}

export interface LadderInputs {
  /** The opponent whose result screen this is; null only if the run had none. */
  opponent: RivalRef | null;
  /** What the player just ran, in metres. */
  playerScoreM: number;
  /**
   * The device's best eligible run on this track, **including the run just
   * played** (`GET /api/self-best`, H02 B3). Null = the server holds no
   * eligible run of yours here at all — which now says something true and
   * useful, instead of "you have only run once".
   */
  selfBest: RivalRef | null;
  /** True when `selfBest` IS the run just finished — a record set seconds ago. */
  selfBestIsThisRun?: boolean;
  /**
   * Has the server answered for this rung yet? Default true (the answer is in
   * hand). False = the screen mounted before the request landed, or the request
   * failed — and since B3 the empty state 「先留下一个成绩」 *asserts* that the
   * server holds no run of yours, which we must not say on no evidence. The
   * row is omitted until we know, exactly as rung 3 is on an empty pool.
   */
  selfKnown?: boolean;
  /** Today's stranger; null when the pool is empty (`GET /api/daily-rival`). */
  daily: RivalRef | null;
}

/** Positive metres still owed, or null once the player is level or ahead. */
function gapTo(target: number, playerScoreM: number): number | null {
  const gap = target - playerScoreM;
  return gap > 0 ? gap : null;
}

/**
 * The ladder for one result screen, top rung first. Truth table:
 *
 * | rung | shown when | clickable | trailing copy |
 * |---|---|---|---|
 * | 刚才的对手 | an opponent was raced (always, in practice) | yes | `gapM` metres, or 「再赢一次」 when the player won or tied |
 * | 本周的你 | once the server has answered (`selfKnown`) | whenever the server holds a run of yours | your record (marked `justSet` when it is the run just finished), or 「先留下一个成绩」 |
 * | 今日路人 | the stranger pool is non-empty | yes | their score + gap |
 *
 * Rung 2 is the only one with an empty state: it is the rung the player can
 * fill by simply running again, so saying so beats hiding it. Rung 3 has no
 * empty state — "there is nobody on this track yet" is not an invitation, it is
 * an apology, so the row is omitted.
 */
export function ladderRungs(input: LadderInputs): Rung[] {
  const rungs: Rung[] = [];

  if (input.opponent) {
    rungs.push({
      id: 'opponent',
      nickname: input.opponent.nickname,
      scoreM: input.opponent.scoreM,
      gapM: gapTo(input.opponent.scoreM, input.playerScoreM),
      clickable: true,
    });
  }

  if (input.selfBest) {
    rungs.push({
      id: 'self',
      nickname: null,
      scoreM: input.selfBest.scoreM,
      gapM: gapTo(input.selfBest.scoreM, input.playerScoreM),
      clickable: true,
      justSet: input.selfBestIsThisRun === true,
    });
  } else if (input.selfKnown !== false) {
    rungs.push({ id: 'self', nickname: null, scoreM: null, gapM: null, clickable: false });
  }

  if (input.daily) {
    rungs.push({
      id: 'daily',
      nickname: input.daily.nickname,
      scoreM: input.daily.scoreM,
      gapM: gapTo(input.daily.scoreM, input.playerScoreM),
      clickable: true,
    });
  }

  return rungs;
}

/** `ladder_shown` prop: which rungs were actually offered, e.g. `"opponent,daily"`. */
export function rungsAvailable(rungs: readonly Rung[]): string {
  return rungs
    .filter((r) => r.clickable)
    .map((r) => r.id)
    .join(',');
}
